/// <amd-module name="Wit/Common/WitSettingsViews/Components/StylesTable/StyleTable" />
import * as React from "react";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IObservableArray, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { css, IVssComponentContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { ColorDropdown, ExpandableColorButton } from "VSSUI/Color";
import { Header, TitleSize } from "VSSUI/Header";
import { IconSize } from "VSSUI/Icon";
import { BoltListDragEvent, IListDropData, IListItemDetails, List, ListDragDropBehavior, ListDragImage, ListItem } from "VSSUI/List";
import { Observer } from "VSSUI/Observer";
import { ITableColumn, Table } from "VSSUI/Table";
import { TextField } from "VSSUI/TextField";
import * as Resources from "Wit/Common/WitSettingsViews/Resources";
import { CriteriaTable } from "../CriteriaTable/CriteriaTable";
import { TableStatus } from "../TableStatus";
import { IStylesRuleData } from "./StyleInterfaces";
import { IStyleService } from "./StyleService";

export function getStyleSettingsTab(
    pageContext: IVssPageContext,
    rules: IStylesRuleData[],
    disabled: boolean,
    groupId?: string,
    onSaveSettings?: () => Promise<void>
) {
    const styleService = pageContext.getService<IStyleService>("IStyleService");
    styleService.setStyleSettingRows(rules);
    return {
        groupId,
        isDirty: styleService.isDirty,
        isValid: styleService.isValid,
        name: Resources.StylesSettingsTab,
        onSaveSettings,
        renderContent: () => <StyleTable disabled={disabled} />
    };
}

export interface StyleTableProps {
    /**
     * Optional class name
     */
    className?: string;
    /**
     * Rule controls will be disabled
     */
    disabled?: boolean;
}

export const maxNumberOfRules = 10;

export class StyleTable extends TfsComponent<StyleTableProps> {
    private _styleSettingsService: IStyleService;
    private collapsed = new Array<ObservableValue<boolean>>();

    constructor(props: StyleTableProps, context: IVssComponentContext) {
        super(props, context);
        this._styleSettingsService = context.pageContext.getService<IStyleService>("IStyleService");
        const rows = this._styleSettingsService.rows;
        // initialize all cards to be collapsed on panel open
        this.collapsed = rows.value.map(() => new ObservableValue<boolean>(false));
    }

    public render(): JSX.Element {
        const { disabled } = this.props;
        const rows = this._styleSettingsService.rows;
        const emptyRows: IObservableArray<IStylesRuleData> = new ObservableArray<IStylesRuleData>();

        return (
            <div className="rhythm-vertical-8 style-table">
                <Header
                    className="padding-vertical-0"
                    description={Resources.StylesSettingsTableDescription}
                    title={Resources.StylesSettingsTab}
                    titleSize={TitleSize.Small}
                />
                <TableStatus
                    className="padding-horizontal-20"
                    dataProviderErrorMessage={this._styleSettingsService.dataProviderErrorMessage}
                    validationErrorMessage={this._styleSettingsService.validationErrorMessage}
                />
                <Observer rows={rows}>
                    {(props: { rows: IStylesRuleData[] }) => (
                        <>
                            <div className="margin-top-8 padding-horizontal-20">
                                <Button
                                    disabled={disabled || rows.length >= maxNumberOfRules}
                                    iconProps={{ iconName: "Add" }}
                                    onClick={this._onAddRule}
                                    text={Resources.AddStyleButtonText}
                                />
                            </div>
                            <Table columns={this._columns} showHeader={rows.length > 0} itemProvider={emptyRows} />
                            <List
                                ariaLabel={Resources.FieldCriteriaHeader}
                                behaviors={disabled ? undefined : [this.dragDropBehavior]}
                                itemProvider={rows}
                                selectRowOnClick={true}
                                width="100%"
                                renderRow={(index: number, item: IStylesRuleData, details: IListItemDetails<IStylesRuleData>) => {
                                    const fieldRows = item.criteriaData;
                                    return (
                                        <ListItem key={index} index={index} details={details}>
                                            <Observer collapsed={this.collapsed[index]}>
                                                {() => (
                                                    <div className="flex-grow flex-column">
                                                        {this.renderHeader(index)}
                                                        {this.collapsed[index].value ? (
                                                            <CriteriaTable
                                                                className="padding-bottom-16"
                                                                disabled={disabled}
                                                                onAddRow={() => this._styleSettingsService.addCriteriaSettingsRow(undefined, index)}
                                                                onDeleteRow={(rowIndex, item) =>
                                                                    this._styleSettingsService.removeCriteriaRow(rowIndex, item)
                                                                }
                                                                onUpdateField={(rowIndex, item, fieldReferenceName) =>
                                                                    this._styleSettingsService.updateField(fieldReferenceName, item)
                                                                }
                                                                onUpdateOperator={(rowIndex, item, operator) =>
                                                                    this._styleSettingsService.updateOperator(operator, item)
                                                                }
                                                                onUpdateValue={(rowIndex, item, fieldValue) =>
                                                                    this._styleSettingsService.updateValue(fieldValue, item)
                                                                }
                                                                rows={fieldRows}
                                                            />
                                                        ) : undefined}
                                                    </div>
                                                )}
                                            </Observer>
                                        </ListItem>
                                    );
                                }}
                            ></List>
                        </>
                    )}
                </Observer>
            </div>
        );
    }

    private onDrop = (event: BoltListDragEvent<HTMLElement, IStylesRuleData>, dropData: IListDropData) => {
        const setting = event.detail.dataTransfer.data;

        this.moveRow(setting, dropData.index);
    };

    private dragDropBehavior = new ListDragDropBehavior({
        allowedTypes: ["ruleName"],
        onDrop: this.onDrop,
        renderDragImage: event => {
            const ruleName = event.detail.dataTransfer.data?.ruleName || "";
            return <ListDragImage text={ruleName.value} />;
        },
        type: "ruleName"
    });

    private moveRow = (setting: IStylesRuleData, dropIndex: number) => {
        // First we remove the item that is being dragged, then we add it in a second operation
        const rows = this._styleSettingsService.rows;
        const draggedRowIndex = rows.value.findIndex(f => f.id === setting.id);

        // update setting list
        rows.splice(draggedRowIndex, 1);
        rows.splice(dropIndex, 0, setting);

        this._styleSettingsService.validateDragAndDrop();
    };

    private _removeRow = (index: number) => {
        this._styleSettingsService.removeRuleRow(index);
    };

    private _onAddRule = () => {
        this._styleSettingsService.addRuleRow();
        this.collapsed.push(new ObservableValue<boolean>(true));
        this._styleSettingsService.addCriteriaSettingsRow();
    };

    private renderHeader = (index: number): JSX.Element => {
        const rows = this._styleSettingsService.rows.value;
        return (
            <div className="flex-row flex-grow flex-center">
                {this._renderNameColumn(index, index, this._columns[0], rows[index])}
                {this._renderColorColumn(index, index, this._columns[1], rows[index])}
                {this._renderTitleStyleColumn(index, index, this._columns[2], rows[index])}
                {this._renderEnabledColumn(index, index, this._columns[3], rows[index])}
                {this._renderDeleteColumn(index, index, this._columns[4], rows[index])}
            </div>
        );
    };

    private _renderNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IStylesRuleData>,
        tableItem: IStylesRuleData
    ): JSX.Element => {
        const onChange = (event: any, value: string) => {
            this._styleSettingsService.updateName(rowIndex, value);
        };

        return (
            <div className="flex-row bolt-table-cell-content column-fields">
                <div className="margin-right-8">
                    <Button
                        ariaLabel={this.collapsed[rowIndex].value ? Resources.CollapseRow : Resources.ExpandRow}
                        iconProps={{ iconName: this.collapsed[rowIndex].value ? "ChevronDown" : "ChevronRight", size: IconSize.small }}
                        onClick={e => {
                            e.preventDefault();
                            this.collapsed[rowIndex].value = !this.collapsed[rowIndex].value;
                        }}
                        subtle
                    />
                </div>
                <TextField
                    containerClassName="name-text-field"
                    disabled={this.props.disabled}
                    onChange={onChange}
                    placeholder={Resources.TextFieldPlaceholder}
                    value={tableItem.ruleName}
                />
            </div>
        );
    };

    private _renderColorColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IStylesRuleData>,
        tableItem: IStylesRuleData
    ): JSX.Element => {
        const onChange = (color: string) => {
            this._styleSettingsService.updateColor(rowIndex, color);
        };

        return (
            <Observer color={tableItem.color}>
                {() => (
                    <div className={"bolt-table-cell-content column-fields"}>
                        <ColorDropdown
                            ariaLabel={Resources.ColorColumnLabel}
                            disabled={this.props.disabled}
                            onColorSelected={onChange}
                            color={tableItem.color.value}
                        />
                    </div>
                )}
            </Observer>
        );
    };

    private _renderTitleStyleColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IStylesRuleData>,
        tableItem: IStylesRuleData
    ): JSX.Element => {
        const updateisTitleBold = () => {
            this._styleSettingsService.updateisTitleBold(rowIndex, !tableItem.isTitleBold.value);
        };

        const updateisTitleItalicized = () => {
            this._styleSettingsService.updateisTitleItalicized(rowIndex, !tableItem.isTitleItalicized.value);
        };

        const updateisTitleUnderlined = () => {
            this._styleSettingsService.updateisTitleUnderlined(rowIndex, !tableItem.isTitleUnderlined.value);
        };

        const updateFontColor = (color: string) => {
            this._styleSettingsService.updateTitleColor(rowIndex, color);
        };

        return (
            <Observer
                isTitleBold={tableItem.isTitleBold}
                isTitleItalicized={tableItem.isTitleItalicized}
                isTitleUnderlined={tableItem.isTitleUnderlined}
            >
                {(observerProps: { isTitleBold: boolean; isTitleItalicized: boolean; isTitleUnderlined: boolean }) => (
                    <div className={"bolt-table-cell-content column-fields"}>
                        <div className="flex-row">
                            <ExpandableColorButton
                                ariaLabel={Resources.ColorColumnLabel}
                                className="padding-top-4"
                                color={tableItem.titleColor.value}
                                onColorSelected={updateFontColor}
                            />
                            <Button
                                ariaLabel={Resources.BoldLabel}
                                className={css("title-styles", observerProps.isTitleBold && "show-enabled-style")}
                                disabled={this.props.disabled}
                                iconProps={{ iconName: "Bold", size: IconSize.medium }}
                                onClick={updateisTitleBold}
                            />
                            <Button
                                ariaLabel={Resources.ItalicizeLabel}
                                className={css("title-styles", observerProps.isTitleItalicized && "show-enabled-style")}
                                disabled={this.props.disabled}
                                iconProps={{ iconName: "Italic", size: IconSize.medium }}
                                onClick={updateisTitleItalicized}
                            />
                            <Button
                                ariaLabel={Resources.UnderlineLabel}
                                className={css("title-styles", observerProps.isTitleUnderlined && "show-enabled-style")}
                                disabled={this.props.disabled}
                                iconProps={{ iconName: "Underline", size: IconSize.medium }}
                                onClick={updateisTitleUnderlined}
                            />
                        </div>
                    </div>
                )}
            </Observer>
        );
    };

    private _renderEnabledColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IStylesRuleData>,
        tableItem: IStylesRuleData
    ): JSX.Element => {
        const onChange = (event: any, value: boolean) => {
            this._styleSettingsService.updateIsEnabled(rowIndex, value);
        };

        return (
            <div className="bolt-table-cell-content enabled-field">
                <Checkbox ariaLabel={Resources.EnabledLabel} checked={tableItem.isEnabled} onChange={onChange} disabled={this.props.disabled} />
            </div>
        );
    };

    private _renderDeleteColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IStylesRuleData>,
        tableItem: IStylesRuleData
    ): JSX.Element => {
        return (
            <div className="bolt-table-cell bolt-list-cell margin-right-8">
                <div className="flex-column flex-center">
                    <Button
                        ariaLabel={Resources.DeleteRowLabel}
                        className="bolt-list-cell-content-reveal padding-horizontal-4"
                        disabled={this.props.disabled}
                        iconProps={{ iconName: "Delete" }}
                        onClick={() => this._removeRow(rowIndex)}
                        subtle={true}
                    />
                </div>
            </div>
        );
    };

    private _columns: ITableColumn<IStylesRuleData>[] = [
        {
            id: "ruleName",
            name: Resources.RuleNameLabel,
            renderCell: this._renderNameColumn,
            width: new ObservableValue(-37)
        },
        {
            id: "color",
            name: Resources.ColorColumnLabel,
            renderCell: this._renderColorColumn,
            width: new ObservableValue(-29)
        },
        {
            id: "title",
            name: Resources.TitleStylesLabel,
            renderCell: this._renderTitleStyleColumn,
            width: new ObservableValue(-29)
        },
        {
            id: "isEnabled",
            name: Resources.EnabledLabel,
            renderCell: this._renderEnabledColumn,
            width: new ObservableValue(125)
        },
        {
            id: "delete",
            renderCell: this._renderDeleteColumn,
            width: new ObservableValue(40)
        }
    ];
}
